// src/components/ReportDamageFlow.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Text,
  Title,
  Flex,
  Box,
  Group,
  Paper,
  Textarea,
  Stack,
  Progress,
  ThemeIcon,
  rem,
  Alert,
} from "@mantine/core";

import {
  TbCamera,
  TbX,
  TbAlertSmall,
  TbAlertTriangle,
  TbCircleCheck,
} from "react-icons/tb";

import { FaWrench } from "react-icons/fa6";

import Lottie from "lottie-react";
import reportingAnimation from "../assets/lottie/reporting.json";
import { reportDamage } from "../atoms/network";

// ─────────────────────────────────────────────────────────────
// small helpers
// ─────────────────────────────────────────────────────────────
const NOTE_LIMIT = 800;
const MAX_UPLOAD_DIM = 1600;     // px (longest side)
const JPEG_QUALITY = 0.78;       // 0..1

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function isLikelyHeic(file) {
  const t = (file?.type || "").toLowerCase();
  if (t.includes("heic") || t.includes("heif")) return true;
  const name = (file?.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
}

/**
 * Create (and clean up) an object URL for any Blob/File
 */
function useObjectUrl(blob) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}

/**
 * Decode an image Blob/File into an ImageBitmap or HTMLImageElement.
 * We try createImageBitmap first (fast path), then fall back to <img>.
 */
async function decodeImage(fileOrBlob) {
  // Fast path: ImageBitmap (not on all browsers)
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(fileOrBlob);
      return { kind: "bitmap", bmp, width: bmp.width, height: bmp.height };
    } catch {
      // fall through
    }
  }

  // Fallback: <img> with object URL
  const url = URL.createObjectURL(fileOrBlob);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = url;
    });
    return { kind: "image", img, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Draw an ImageBitmap or HTMLImageElement to a canvas with max dimension constraint.
 */
function drawToCanvas(source, width, height, maxDim = MAX_UPLOAD_DIM) {
  const scale = Math.min(1, maxDim / Math.max(width, height));
  const cw = Math.max(1, Math.round(width * scale));
  const ch = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");

  if (source.kind === "bitmap") {
    ctx.drawImage(source.bmp, 0, 0, cw, ch);
  } else {
    ctx.drawImage(source.img, 0, 0, cw, ch);
  }
  return canvas;
}

/**
 * Convert arbitrary image (including HEIC where supported) to a displayable JPEG Blob,
 * primarily for previews (so <img> can show it even if original is HEIC).
 */
async function normalizeToDisplayableJpeg(fileOrBlob, maxDim = 1200, quality = 0.82) {
  const decoded = await decodeImage(fileOrBlob);
  const canvas = drawToCanvas(decoded, decoded.width, decoded.height, maxDim);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  // Ensure a File for uniform handling (previews, uploads)
  return new File([blob], "photo.jpg", { type: "image/jpeg" });
}

/**
 * Resize to JPEG base64 string (without data: prefix), ready for upload.
 */
async function fileToResizedBase64(fileOrBlob, maxDim = MAX_UPLOAD_DIM, quality = JPEG_QUALITY) {
  if (!fileOrBlob) return null;
  // If it's HEIC and the browser can't decode via <img>, createImageBitmap might still handle it.
  const decoded = await decodeImage(fileOrBlob);
  const canvas = drawToCanvas(decoded, decoded.width, decoded.height, maxDim);
  const dataUrl = canvas.toDataURL("image/jpeg", quality); // "data:image/jpeg;base64,...."
  return dataUrl.split(",")[1]; // strip prefix
}

function Bullet({ icon, children }) {
  return (
    <Flex gap="sm" align="center">
      <ThemeIcon variant="light" color="blue" radius="xl" size="lg">
        {icon}
      </ThemeIcon>
      <Text size="sm">{children}</Text>
    </Flex>
  );
}

// ─────────────────────────────────────────────────────────────
// PhotoPicker – robust click + drag/drop + mobile camera
// ─────────────────────────────────────────────────────────────
function PhotoPicker({ value, onChange, label }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const previewUrl = useObjectUrl(value);

  const isMobile =
    typeof navigator !== "undefined" &&
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  function openFilePicker(e) {
    e.preventDefault();
    inputRef.current?.click();
  }

  async function handlePickedFile(file) {
    if (!file) return;
    try {
      if (!file.type?.startsWith("image/") && !isLikelyHeic(file)) {
        throw new Error("This file is not an image.");
      }
      // Convert to displayable JPEG for reliable preview in all browsers
      const normalized = await normalizeToDisplayableJpeg(file);
      onChange(normalized);
    } catch (err) {
      console.warn("Image pick failed:", err);
      alert(
        err?.message?.includes("image")
          ? "Please select an image file."
          : "We could not process that photo. Try another image or pick from your library."
      );
    }
  }

  async function onInputChange(e) {
    const f = e.target.files?.[0];
    // allow the same file to be selected again after deletion
    e.target.value = "";
    await handlePickedFile(f);
  }

  async function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    await handlePickedFile(f);
  }

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={{
        position: "relative",
        width: rem(110),
        height: rem(110),
        borderRadius: rem(14),
        overflow: "hidden",
        border: `1px solid var(--mantine-color-${dragOver ? "blue-5" : "gray-4"})`,
        outline: dragOver ? "2px dashed var(--mantine-color-blue-5)" : "none",
        background: "rgba(0,0,0,0.03)",
      }}
    >
      <button
        type="button"
        onClick={openFilePicker}
        style={{
          all: "unset",
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
        }}
        aria-label={label}
      >
        {!previewUrl ? (
          <Stack gap={4} align="center">
            <TbCamera size={20} />
            <Text size="xs" c="dimmed">
              {label}
            </Text>
            {!isMobile && (
              <Text size="xs" c="dimmed">
                or drop
              </Text>
            )}
          </Stack>
        ) : (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={previewUrl}
            alt="photo preview"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </button>

      {/* Hidden input is triggered programmatically via ref (reliable in modals) */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        {...(isMobile ? { capture: "environment" } : {})}
        onChange={onInputChange}
        style={{ position: "absolute", inset: 0, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
      />

      {value && (
        <Button
          size="compact-xs"
          variant="subtle"
          color="dark"
          onClick={() => onChange(null)}
          style={{
            position: "absolute",
            top: rem(4),
            right: rem(4),
            paddingInline: rem(6),
            height: rem(22),
            backdropFilter: "blur(4px)",
          }}
          title="Remove"
          aria-label="Remove photo"
        >
          <TbX size={14} />
        </Button>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function ReportDamageFlow({
  opened,
  onClose,
  siteID,
  itemID,
  phone,
  userID = localStorage.getItem("userID") || "0",
}) {
  const [step, setStep] = useState("intro"); // "intro" | "form"
  const [note, setNote] = useState("");
  const [img1, setImg1] = useState(null); // File/Blob (normalized JPEG for preview & upload)
  const [img2, setImg2] = useState(null);
  const [img3, setImg3] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const canSubmit = useMemo(
    () => note.trim().length > 0 || !!img1 || !!img2 || !!img3,
    [note, img1, img2, img3]
  );

  const resetAll = () => {
    setStep("intro");
    setNote("");
    setImg1(null);
    setImg2(null);
    setImg3(null);
    setBusy(false);
    setError("");
    setDone(false);
  };

  const closeAndReset = () => {
    onClose?.();
    resetAll();
  };

  async function handleSubmit() {
    if (busy) return;
    if (!canSubmit) {
      setError("Please add a note or at least one photo.");
      return;
    }

    try {
      setBusy(true);
      setError("");

      // Convert selected photos to resized JPEG base64 (server-friendly)
      const [p1, p2, p3] = await Promise.all([
        img1 ? fileToResizedBase64(img1) : null,
        img2 ? fileToResizedBase64(img2) : null,
        img3 ? fileToResizedBase64(img3) : null,
      ]);

      const res = await reportDamage({
        siteID,
        itemID,
        note,
        phone,
        photo1Base64: p1,
        photo2Base64: p2,
        photo3Base64: p3,
      });

      if (res?.status) {
        setDone(true);
      } else {
        throw new Error(res?.message || "Server rejected the report.");
      }
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={closeAndReset}
      withCloseButton={!busy}
      fullScreen
      closeOnClickOutside={!busy}
      radius="md"
      padding="lg"
      transitionProps={{ transition: "slide-up", duration: 200 }}
      styles={{ body: { paddingTop: rem(8), paddingBottom: rem(8) } }}
      title={step === "intro" ? "Report a Fault" : done ? "Submitted" : "Describe the Issue"}
    >
      {/* INTRO */}
      {step === "intro" && (
        <Stack gap="sm" pt="sm">
          <Flex justify="center">
            <ThemeIcon size={48} radius="xl" color="blue" variant="gradient" gradient={{ from: "blue", to: "cyan" }}>
              <FaWrench />
            </ThemeIcon>
          </Flex>

          <Title order={3} ta="center">
            Help us keep facilities in great condition.
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            It takes less than a minute to report a fault. Photos are optional but very helpful.
          </Text>

          <Flex justify="center">
            <Box  mb={-75} mt={-70} style={{ width: 320, maxWidth: "100%"}}>
              <Lottie animationData={reportingAnimation} loop />
            </Box>
          </Flex>

          <Paper radius="lg" withBorder p="md">
            <Stack gap="sm">
              <Bullet icon={<TbCamera size={16} />}>Add up to 3 photos</Bullet>
              <Bullet icon={<TbAlertTriangle size={16} />}>Tell us what’s wrong</Bullet>
              <Bullet icon={<TbCircleCheck size={16} />}>We’ll notify maintenance</Bullet>
            </Stack>
          </Paper>

          <Button size="md" onClick={() => setStep("form")} fullWidth>
            Start report
          </Button>
          <Button variant="subtle" onClick={closeAndReset} fullWidth>
            Cancel
          </Button>
        </Stack>
      )}

      {/* FORM */}
      {step === "form" && !done && (
        <Stack gap="lg" pt="xs">
          {error && (
            <Alert color="red" icon={<TbAlertTriangle />}>
              {error}
            </Alert>
          )}

          <Paper radius="lg" withBorder p="md">
            <Group justify="space-between" mb="xs">
              <Text fw={600}>Photos</Text>
              <Text size="xs" c="dimmed">
                Optional • Up to 3
              </Text>
            </Group>
            <Group gap="md" wrap="nowrap">
              <PhotoPicker value={img1} onChange={setImg1} label="Add" />
              <PhotoPicker value={img2} onChange={setImg2} label="Add" />
              <PhotoPicker value={img3} onChange={setImg3} label="Add" />
            </Group>
          </Paper>

          <Paper radius="lg" withBorder p="md">
            <Text fw={600} mb="xs">
              Notes
            </Text>
            <Textarea
              minRows={5}
              placeholder="Describe the damage (what, where, severity)…"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value.slice(0, NOTE_LIMIT))}
            />
            <Group justify="space-between" mt="xs">
              <Text size="xs" c={note.length >= NOTE_LIMIT ? "red" : "dimmed"}>
                {note.length}/{NOTE_LIMIT}
              </Text>
              <Text size="xs" c="dimmed">
                Includes device time.
              </Text>
            </Group>
          </Paper>

          <Button fullWidth color="red" onClick={handleSubmit} loading={busy} disabled={!canSubmit || busy}>
            Submit report
          </Button>
          <Button variant="subtle" onClick={closeAndReset} disabled={busy} fullWidth>
            Cancel
          </Button>

          {busy && (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Uploading photos…
              </Text>
              <Progress value={70} animated />
            </Box>
          )}
        </Stack>
      )}

      {/* SUCCESS */}
      {done && (
        <Stack gap="md" align="center" pt="md">
          <ThemeIcon size={64} radius="xl" color="teal" variant="light">
            <TbCircleCheck size={36} />
          </ThemeIcon>
          <Title order={3} ta="center">
            Thanks!
          </Title>
          <Text ta="center" c="dimmed">
            Your damage report has been submitted.
          </Text>
          <Button onClick={closeAndReset} fullWidth>
            Done
          </Button>
        </Stack>
      )}
    </Modal>
  );
}
